import { Plus } from "@phosphor-icons/react";
import { useMemo, useState, type ReactNode } from "react";
import styled from "@emotion/styled";
import type { CSSObject } from "@emotion/react";
import { twRing } from "@/lib/emotionTw";

export type SelectableChipOption = {
  value: string;
  label: ReactNode;
  icon?: ReactNode;
  disabled?: boolean;
};

export type SelectableChipAction = {
  label: ReactNode;
  disabled?: boolean;
  onClick?: () => void;
};

export type SelectableChipGroupProps = {
  options: SelectableChipOption[];
  value?: string | string[];
  defaultValue?: string | string[];
  multiple?: boolean;
  onValueChange?: (value: string | string[]) => void;
  addAction?: SelectableChipAction;
  className?: string;
  "aria-label"?: string;
};

/* Exact compiled output of the pre-migration Tailwind utilities, in the same
 * class-list emission order. Verified against the frozen baseline. The
 * selected/unselected class sets are exclusive branches (border/background/
 * text color), matching the old ternary. `bfrs-text-xs` carries its own
 * line-height (1rem) — no later leading class, so it stands. */

const SelectableChipGroupRoot = styled("div", {
  label: "AgenticSelectableChipGroup-root"
})({
  display: "flex",
  flexWrap: "wrap",
  gap: ".5rem"
});

const chipBase: CSSObject = {
  display: "inline-flex",
  minHeight: "2rem",
  alignItems: "center",
  gap: ".375rem",
  borderRadius: "var(--bfrs-radius-pill)",
  borderWidth: "1px",
  paddingLeft: ".75rem",
  paddingRight: ".75rem",
  fontSize: ".75rem",
  lineHeight: "1rem",
  fontWeight: 600,
  outline: "2px solid transparent",
  outlineOffset: "2px",
  transitionProperty:
    "color,background-color,border-color,text-decoration-color,fill,stroke,opacity,box-shadow,transform,filter,backdrop-filter",
  transitionTimingFunction: "cubic-bezier(.4,0,.2,1)",
  transitionDuration: ".15s",
  "&:focus-visible": twRing("2px", "hsl(var(--bfrs-color-border-focus) / .25)"),
  "&:disabled": {
    cursor: "not-allowed",
    opacity: ".55"
  }
};

const SelectableChip = styled("button", {
  label: "AgenticSelectableChipGroup-chip",
  shouldForwardProp: (propName) => propName !== "ownerState"
})<{ ownerState: { selected: boolean } }>(({ ownerState }) => [
  chipBase,
  ownerState.selected
    ? {
        "--tw-border-opacity": "1",
        borderColor: "hsl(var(--bfrs-color-primary) / var(--tw-border-opacity, 1))",
        "--tw-bg-opacity": "1",
        backgroundColor: "hsl(var(--bfrs-color-primary) / var(--tw-bg-opacity, 1))",
        "--tw-text-opacity": "1",
        color: "hsl(var(--bfrs-color-primary-contrast) / var(--tw-text-opacity, 1))"
      }
    : {
        "--tw-border-opacity": "1",
        borderColor: "hsl(var(--bfrs-color-form-control-border) / var(--tw-border-opacity, 1))",
        "--tw-bg-opacity": "1",
        backgroundColor: "hsl(var(--bfrs-color-surface-card) / var(--tw-bg-opacity, 1))",
        "--tw-text-opacity": "1",
        color: "hsl(var(--bfrs-color-text-secondary) / var(--tw-text-opacity, 1))",
        "&:hover": {
          borderColor: "hsl(var(--bfrs-color-primary) / .6)",
          "--tw-bg-opacity": "1",
          backgroundColor: "hsl(var(--bfrs-color-primary-soft) / var(--tw-bg-opacity, 1))"
        }
      }
]);

const SelectableChipAddButton = styled("button", {
  label: "AgenticSelectableChipGroup-addButton"
})([
  chipBase,
  {
    borderStyle: "dashed",
    "--tw-border-opacity": "1",
    borderColor: "hsl(var(--bfrs-color-form-control-border) / var(--tw-border-opacity, 1))",
    "--tw-bg-opacity": "1",
    backgroundColor: "hsl(var(--bfrs-color-surface-card) / var(--tw-bg-opacity, 1))",
    "--tw-text-opacity": "1",
    color: "hsl(var(--bfrs-color-primary) / var(--tw-text-opacity, 1))",
    "&:hover": {
      "--tw-border-opacity": "1",
      borderColor: "hsl(var(--bfrs-color-primary) / var(--tw-border-opacity, 1))",
      "--tw-bg-opacity": "1",
      backgroundColor: "hsl(var(--bfrs-color-primary-soft) / var(--tw-bg-opacity, 1))"
    }
  }
]);

const SelectableChipLabel = styled("span", {
  label: "AgenticSelectableChipGroup-chipLabel"
})({
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap"
});

const toArray = (value: string | string[] | undefined) => {
  if (Array.isArray(value)) return value;
  return value ? [value] : [];
};

export function SelectableChipGroup({ options, value, defaultValue, multiple, onValueChange, addAction, className, "aria-label": ariaLabel }: SelectableChipGroupProps) {
  const [internalValue, setInternalValue] = useState<string[]>(() => toArray(defaultValue));
  const selectedValues = useMemo(() => new Set(toArray(value ?? internalValue)), [internalValue, value]);

  const commitValue = (nextValues: string[]) => {
    const normalized = Array.from(new Set(nextValues));
    if (value === undefined) setInternalValue(normalized);
    onValueChange?.(multiple ? normalized : normalized[0] ?? "");
  };

  const toggleValue = (nextValue: string) => {
    if (multiple) {
      commitValue(selectedValues.has(nextValue) ? Array.from(selectedValues).filter((item) => item !== nextValue) : [...selectedValues, nextValue]);
      return;
    }

    commitValue([nextValue]);
  };

  return (
    <SelectableChipGroupRoot role={multiple ? "group" : "radiogroup"} aria-label={ariaLabel} className={className}>
      {options.map((option) => {
        const selected = selectedValues.has(option.value);
        return (
          <SelectableChip
            key={option.value}
            type="button"
            role={multiple ? "checkbox" : "radio"}
            aria-checked={selected}
            disabled={option.disabled}
            ownerState={{ selected }}
            onClick={() => toggleValue(option.value)}
          >
            {option.icon}
            <SelectableChipLabel>{option.label}</SelectableChipLabel>
          </SelectableChip>
        );
      })}
      {addAction && (
        <SelectableChipAddButton type="button" disabled={addAction.disabled} onClick={addAction.onClick}>
          <Plus size={13} weight="bold" />
          <SelectableChipLabel>{addAction.label}</SelectableChipLabel>
        </SelectableChipAddButton>
      )}
    </SelectableChipGroupRoot>
  );
}
